import { QueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { ReactNode, createContext, useContext } from "react";

import api from "../config/api.config";
import { api_url } from "../config/api-endpoint";

export interface TrialLesson {
  name: string;
  type: string;
  languageId: number;
  description: string;
  price: number;
  duration: number;
}

export interface LessonResponse {
  statusCode: number;
  status: string;
  message: string;
  result: {
    id: string;
    teacherId: string;
    title: string;
    description: string;
    languageId: number;
    type: string;
    duration: number;
    status: string;
    createdAt: string;
    updatedAt: string;
    price: number;
  };
}

export interface Language {
  language: string;
  proficiency: string;
  status: string;
}

export interface ApprovedLanguagesResponse {
  statusCode: number;
  status: string;
  message: string;
  result: Language[];
}

interface TrialLessonContextType {
  isTrialLessonCreated: boolean;
  teacherLanguagesArr: Language[];
  saveLesson: (lesson: TrialLesson) => Promise<TrialLesson>;
}

const TrialLessonContext = createContext<TrialLessonContextType | undefined>(
  undefined,
);

const queryClient = new QueryClient();

export const TrialLessonProvider = ({ children }: { children: ReactNode }) => {
  const token = localStorage.getItem("token");
  const merchantId = localStorage.getItem("teacherId");

  // Check if teacher has trail lesson.
  const { data: hasTrial, isLoading } = useQuery({
    queryKey: ["trialLessonCreated", merchantId],
    queryFn: async () => {
      const { data } = await api.get<LessonResponse>(
        `${api_url}/teachers/lessons/trail`,
      );
      return data.status === "success" && data.statusCode === 200;
    },
    staleTime: 0,
    retry: false,
    enabled: !!token && !!merchantId,
  });

  const { data: teacherLanguages = [] } = useQuery<Language[]>({
    queryKey: ["teacherLanguageTaught", merchantId],
    queryFn: async () => {
      const { data } = await api.get<ApprovedLanguagesResponse>(
        `${api_url}/teachers/${merchantId}/languages-taught`,
      );
      return data.result;
    },
    staleTime: 5 * 1000,
    retry: false,
    enabled: !!token && !!merchantId,
  });

  const saveLessonMutation = useMutation({
    mutationFn: async (newLesson: TrialLesson) => {
      await api.post(`${api_url}/lessons/create`, newLesson);
      return newLesson;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["trialLessonCreated", merchantId],
      });
      queryClient.setQueryData(["trialLessonCreated", merchantId], true);
    },
  });

  if (isLoading) {
    return null;
  }

  return (
    <TrialLessonContext.Provider
      value={{
        isTrialLessonCreated: true, //Boolean(hasTrial),
        teacherLanguagesArr: teacherLanguages,
        saveLesson: saveLessonMutation.mutateAsync,
      }}
    >
      {children}
    </TrialLessonContext.Provider>
  );
};

export const useTrialLesson = () => {
  const ctx = useContext(TrialLessonContext);
  if (!ctx) {
    throw new Error("useTrialLesson must be used inside TrialLessonProvider");
  }
  return ctx;
};
